// src/lib/auth.ts + AuthContext
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import {
  onAuthStateChanged, signInWithPopup, signInWithRedirect, getRedirectResult,
  signOut as firebaseSignOut,
  signInWithEmailAndPassword, sendPasswordResetEmail,
  GoogleAuthProvider, User,
} from "firebase/auth";
import { auth } from "./firebase";
import { getUser } from "./firestore";
import { getIntegration } from "./integrations";
import { HelpDeskUser, GwsIntegrationConfig } from "@/types";

interface AuthState {
  firebaseUser: User | null;
  helpDeskUser: HelpDeskUser | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

import React from "react";
export const AuthContext = createContext<AuthState>({} as AuthState);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [helpDeskUser, setHelpDeskUser] = useState<HelpDeskUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Surface any errors from a completed redirect sign-in. The actual
    // session is picked up by onAuthStateChanged below — this call is
    // mostly for catching/logging redirect failures.
    getRedirectResult(auth).catch((e) => {
      console.error("Google redirect sign-in failed:", e);
    });

    const unsub = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);
      if (user) {
        const hd = await getUser(user.uid);
        setHelpDeskUser(hd);
      } else {
        setHelpDeskUser(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    // Pull allowed-domain from the GWS integration config (publicly readable)
    // and fall back to the env var if Firestore is unreachable pre-auth.
    let allowedDomain = import.meta.env.VITE_GOOGLE_WORKSPACE_DOMAIN ?? "";
    try {
      const gws = await getIntegration<GwsIntegrationConfig>("gws");
      if (gws.config.allowedDomain) allowedDomain = gws.config.allowedDomain;
    } catch { /* fall back to env var */ }
    if (allowedDomain) provider.setCustomParameters({ hd: allowedDomain });

    // Mobile browsers (iOS Safari, in-app webviews, anything with strict
    // third-party cookie policies) reliably break popup auth. Use redirect
    // there. Desktop keeps the popup for a snappier feel.
    const ua = navigator.userAgent || "";
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|Mobile|webOS/i.test(ua);
    if (isMobile) {
      await signInWithRedirect(auth, provider);
      return; // page navigates away — onAuthStateChanged fires on return
    }
    await signInWithPopup(auth, provider);
  };

  const signInWithEmail = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const sendPasswordReset = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  return React.createElement(
    AuthContext.Provider,
    { value: { firebaseUser, helpDeskUser, loading, signInWithGoogle, signInWithEmail, sendPasswordReset, signOut } },
    children
  );
}

export const useAuth = () => useContext(AuthContext);
